import { Box, Grid, SelectChangeEvent } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ShepherdTour } from 'react-shepherd';
import {
  ICandidate,
  IJobCandidateInteraction,
  amplitude,
  getSubmissionSettings,
  ISubmissionSettingsResponse,
  ICommunicationType,
} from 'wisestep-api';
import {
  WsPagination,
  formattedDateISO,
  WsFilterTags,
  IFilterList,
  WsListingLoader,
  WsDrawer,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../../src/context';
import BulkEmail from '../../../components/BulkEmail';
import BulkSmsDrawer from '../../../components/BulkSms/BulkSmsDrawer';
import {
  workAuthorization,
  workAuthorizationCategory,
} from '../../../components/filters/components/AdvanceFilterHelper';
import TalentPoolAdvanceFilterFormProps from '../../../components/filters/models/TalentPoolAdvanceFiltersFormProps';
import TalentPoolAdvancesFiltersNew from '../../../components/filters/talentpoolFilters/TalentPoolAdvancesFiltersNew';
import {
  getInteraction,
  LayoutTheme,
  pushTalentPoolFilters,
  setDefaultCandidateSearchFilters,
  setDefaultCandidateAdvanceFilters,
  firingAmplitudeEvents,
  getCountryByLocation,
  generateBackwardURL,
} from '../../../features/candidates/utils/CandidateHelper';
import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvPages } from '../../../features/conversationWidget/utils/cvHelper';
import { handleCandidateDownload } from '../../../features/dataDownload/helper/PayloadHelper';
import { useJob } from '../../../features/jobs/hooks';
import useKeyWordGenerator, { Mode } from '../../../hooks/useKeyWordGenerator';
import ErrorLogger from '../../../providers/ErrorLogger';
import { cacheJob, getCachedJob } from '../../../utils/cacheService';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { tourOptions, talentpoolSteps } from '../../../utils/TourStepsAndOptions';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateBulkSubmissions from '../../conversationWidget/components/BulkSubmission/CandidateBulkSubmissions';
import JobSelection from '../../conversationWidget/components/JobSelection';
import ShareCandidateOverlay from '../../conversationWidget/components/ShareCandidateOverlay';
import JobPageParams from '../../jobs/types/JobPageParams';
import { handleSelectedCandidatesLimit } from '../../jobsCrm/components/helper/SendSmsEmailFuntionality';
import ActionsFiltersWithTitle from '../components/actionsFiltersWithTitle';
import CandidateBulkSelectionBar from '../components/candidateBulkSelectionBar';

import { CandidateSearchDefaultRequest } from './CandidateSearchDefaultRequest';
import TalentPoolCandidateCard from './components/candidateCard';
import TalentPoolSearch from './components/search';
import { TalentPoolSearchRequest } from './components/search/TalentPoolSearchRequest';
import TalentpoolHeader from './components/talentpoolHeader';
import useTalentPoolSearch from './hooks/useTalentPoolSearch';
import NoDataMsg from './NoDataMsg';

const TalentPoolListing = () => {
  const [isToggleButtonActive, setIsToggleButtonActive] = useState(true);
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const params = useParams<JobPageParams>();
  const workspace = useWorkspaceContext();
  const [job] = useJob(params.jobId ?? '');
  const searchQuery = useKeyWordGenerator(Mode.TALENT_POOL, job);
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const defaultRequest = { ...CandidateSearchDefaultRequest };
  defaultRequest.jobIds = job?.id ?? '';
  defaultRequest.jobCode = job?.jobCode ?? '';
  defaultRequest.candidateLocations = getCountryByLocation(job?.locations ?? []);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryData = searchParams.get('d') ?? '';
  const [query, setQuery] = useState<IJobCandidateInteraction>(
    queryData ? getDecodedQueryParams(queryData) : defaultRequest
  );
  const [loader, setLoader] = useState<boolean>(false);
  const [tempPageValue, setTempPageValue] = useState<number>(0);
  const candidates = useTalentPoolSearch(query, true, setLoader);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);

  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const navigation = useConversationWidgetStore((state) => state.navigate);
  const navigate = useNavigate();
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>((query?.page || 0) + 1);
  const updateSelectedJob = conversationWidgetStore((state) => state.updateSelectedJob);
  const [advancedFilterDefaultValues, setAdvancedFilterFields] = useState<
    Partial<TalentPoolAdvanceFilterFormProps>
  >(setDefaultCandidateAdvanceFilters(query) ?? {});

  const [searchFilterDefaultValues, setSearchFilterDefaultValues] =
    useState<TalentPoolSearchRequest>(setDefaultCandidateSearchFilters(query) ?? {});
  const setSubmissionSettings = async () => {
    const setting = await getSubmissionSettings();
    setSubmissionSetting(setting.data);
  };
  const [submissionSetting, setSubmissionSetting] = useState<ISubmissionSettingsResponse | null>(
    null
  );
  const [openSubmitCandidate, setOpenSubmitCandidate] = useState(false);
  const [openJob, setOpenJob] = useState<boolean>(false);
  const updateActionChangeJob = useConversationWidgetStore((state) => state.changeJob);
  const setupdateActionChangeJob = useConversationWidgetStore((state) => state.setChangeJob);
  useEffect(() => {
    if (searchQuery) {
      setQuery((queryData) => ({
        ...queryData,
        query: searchQuery,
        candidateLocations: getCountryByLocation(job?.locations ?? []),
      }));
      setSearchFilterDefaultValues((queryData) => ({
        ...queryData,
        query: searchQuery,
        locations: getCountryByLocation(job?.locations ?? []),
      }));
    }
  }, [job?.locations, searchQuery]);
  useEffect(() => {
    const redirectionFlag = searchParams.get('redirection') ?? false;
    const savedPosition = sessionStorage.getItem('scrollPosition');
    if (candidates.totalCandidates > 0 && savedPosition && redirectionFlag) {
      window.scrollTo(0, parseInt(savedPosition, 10));
      sessionStorage.removeItem('scrollPosition');
    }
  }, [candidates.totalCandidates, searchParams]);
  useEffect(() => {
    if (advancedFilterDefaultValues) {
      setAppliedFilters(pushTalentPoolFilters(advancedFilterDefaultValues));
    }
  }, [advancedFilterDefaultValues]);
  useEffect(() => {
    if (navigationState && navigation && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= candidates.list.length - 1 && navigation == 'prev') {
        const index = cvIndex - 1;
        // updateFromPage('talentpool');
        setCvIndex(index);
        // openModal(candidates.list[index]);
      } else if (cvIndex >= 0 && cvIndex < candidates.list.length - 1 && navigation == 'next') {
        const index = cvIndex + 1;
        // updateFromPage('talentpool');
        setCvIndex(index);
        // openModal(candidates.list[index]);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    candidates.list,
    cvIndex,
    navigation,
    navigationState,
    // openModal,
    setIsWidgetOpen,
    updateFromPage,
    updateNavigationState,
  ]);

  const pageChange = (event, value) => {
    setTempPageValue(value);
    unselectAllCandidates();
    const updatedQuery = { ...query };
    updatedQuery.page = value;
    setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    setPageNumber(value + 1);
    if (value < tempPageValue) {
      amplitude('Talentpool_Navigation_Previous', {
        clickedOn: 'Previous Button',
        workspaceName: workspace.name,
        pageName: 'Candidates',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      });
    } else if (value > tempPageValue) {
      amplitude('Talentpool_Navigation_Previous', {
        clickedOn: 'Next Button',
        workspaceName: workspace.name,
        pageName: 'Candidates',
        planType: workspace.planType,
        interfaceType: 'web',
        pageUrl: window.location.href,
      });
    }
  };
  const smartSearch = (data: TalentPoolSearchRequest): IJobCandidateInteraction => {
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    updatedQuery.candidateAllLocationFields = data.locations ?? [];
    updatedQuery.candidateLocations = data.locations ?? [];
    updatedQuery.radiusInMiles = data.distance;
    if (data.candidateLocationPref === 'Current') {
      updatedQuery.searchInCurrentLocation = true;
      updatedQuery.searchInPreferredLocation = false;
    } else if (data.candidateLocationPref === 'Preferred') {
      updatedQuery.searchInCurrentLocation = false;
      updatedQuery.searchInPreferredLocation = true;
    } else {
      updatedQuery.searchInCurrentLocation = true;
      updatedQuery.searchInPreferredLocation = true;
    }
    if (data.query !== undefined && data.query.length > 0) {
      const srt = data.sort ? data.sort.split(',') : ['desc'];
      updatedQuery.sort = [`_score,${srt[1] ?? 'desc'}`];
    } else {
      updatedQuery.sort = [data.sort ?? 'createdOn,desc'];
    }
    updatedQuery.recruiterActions = data.recruiterActions;
    updatedQuery.candidateResponses = data.candidateResponses;
    updatedQuery.query = data.query;
    updatedQuery.specificSearch = data.specificSearch;
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    return updatedQuery;
  };
  const search = (data: TalentPoolSearchRequest) => {
    setSearchFilterDefaultValues(data);
    if (!isEmpty(data.query) && isToggleButtonActive) {
      data.specificSearch = true;
    } else {
      data.specificSearch = false;
    }
    const updatedQuery = smartSearch(data);
    setPageNumber(1);
    setSearchParams({ d: getEncodedQueryParams(updatedQuery) });
    setQuery(updatedQuery);
    firingAmplitudeEvents('Talentpool_Search', 'Search', workspace, 'Candidates');
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: Partial<TalentPoolAdvanceFilterFormProps> =
        advancedFilterDefaultValues;

      if (
        idOfChip == 'totalYearsExperienceStartRange' ||
        idOfChip == 'totalYearsExperienceEndRange'
      ) {
        advancedFilterDefaults['totalYearsExperienceStartRange'] = 0;
        advancedFilterDefaults['totalYearsExperienceEndRange'] = 0;
        advancedFilterDefaultValues['experienceComparisonOperator'] = 'isBetween';
      } else if (['jobType'].includes(idOfChip)) advancedFilterDefaults[idOfChip] = null;
      else advancedFilterDefaults[idOfChip] = [];
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setSearchParams({ d: getEncodedQueryParams(updateQuery(advancedFilterDefaults)) });
      setQuery(updateQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };
  const queryRefresh = () => setQuery({ ...query });
  const updateQuery = (
    data: TalentPoolAdvanceFilterFormProps,
    searchData?: TalentPoolSearchRequest
  ) => {
    const updatedQuery = searchData ? smartSearch(searchData) : { ...query };
    [
      'emailAvailability',
      'phoneAvailability',
      'jobPreferenceContract',
      'jobPreferencePermanent',
      'commPreferencePhone',
      'commPreferenceEmail',
    ].forEach((field) => delete updatedQuery[field]);
    updatedQuery.page = 0;
    setPageNumber(1);
    updatedQuery.jobTitles = data.jobTitles ?? [];
    updatedQuery.jobType = data.jobType;
    updatedQuery.sources = data.source ?? [];
    updatedQuery.languages = data.languages ?? [];
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.currentEmployers = data.currentEmployers;
    updatedQuery.employers = data.previousEmployers ?? [];
    updatedQuery.workAuthorizationCategories = [];
    updatedQuery.workAuthorizationCategories =
      data.workAuthorization?.flatMap((obj) => {
        const value = workAuthorizationCategory[obj as keyof typeof workAuthorizationCategory];
        return value ? [value, value.toUpperCase()] : [];
      }) || [];

    updatedQuery.minSalary = data.salaryRange?.min ?? 0;
    updatedQuery.maxSalary = data.salaryRange?.max ?? 0;
    updatedQuery.salaryCurrency = data.salaryRange?.currency ?? null;
    updatedQuery.salaryTimeSpan = data.salaryRange?.unitTime ?? null;
    if (searchData) updatedQuery.query = searchData?.query;
    updatedQuery.fromDateTime = data.createBWDates?.fromDateName
      ? formattedDateISO(data.createBWDates?.fromDateName)
      : '';
    updatedQuery.toDateTime = data.createBWDates?.toDateName
      ? formattedDateISO(data.createBWDates?.toDateName)
      : '';
    if (updatedQuery.fromDateTime != null && !isEmpty(updatedQuery.fromDateTime)) {
      updatedQuery.fromDateTime = new Date(updatedQuery.fromDateTime);
      updatedQuery.fromDateTime.setHours(0, 0, 0);
      updatedQuery.fromDateTime = formattedDateISO(updatedQuery.fromDateTime);
      updatedQuery.datesUnionBy = 'CREATED_BETWEEN';
    }
    if (updatedQuery.toDateTime != null && !isEmpty(updatedQuery.toDateTime)) {
      updatedQuery.toDateTime = new Date(updatedQuery.toDateTime);
      updatedQuery.toDateTime.setHours(23, 59, 59);
      updatedQuery.toDateTime = formattedDateISO(updatedQuery.toDateTime);
    }
    data.commPreference?.forEach((obj) => {
      if (obj == 'Email') {
        updatedQuery.commPreferenceEmail = true;
        updatedQuery.commType = ICommunicationType.Email;
      } else if (obj == 'Phone') {
        updatedQuery.commPreferencePhone = true;
        updatedQuery.commType = ICommunicationType.Phone;
      } else if (obj == 'Both') {
        updatedQuery.commPreferencePhone = true;
        updatedQuery.commPreferenceEmail = true;
        updatedQuery.commType = ICommunicationType.All;
      }
    });
    data.jobPreference?.forEach((obj) => {
      if (obj == 'Contract') updatedQuery.jobPreferenceContract = true;
      else if (obj == 'Permanent') updatedQuery.jobPreferencePermanent = true;
    });
    data.contactAvailability?.forEach((obj) => {
      if (obj == 'Email') updatedQuery.emailAvailability = true;
      else if (obj == 'Phone') updatedQuery.phoneAvailability = true;
      else if (obj == 'Both') {
        updatedQuery.phoneAvailability = true;
        updatedQuery.emailAvailability = true;
      }
    });
    updatedQuery.noticePeriodMaxDays = data.noticePeriod?.max ?? 0;
    updatedQuery.noticePeriodMinDays = data.noticePeriod?.min ?? 0;
    updatedQuery.languages = data.languages ?? [];
    updatedQuery.clientNames = data.clientNames;
    data.teams?.forEach((obj) => updatedQuery.searchAcrossTeams?.push(obj.id ?? ''));
    data.candidateStatus?.forEach((obj) =>
      updatedQuery.candidateSearchTypeList?.push(obj.toLowerCase())
    );
    updatedQuery.currentStageCurrentStatusPairs = data.stageStatus;
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.candidateSearchTypeList = data.candidateStatus;
    updatedQuery.experienceComparisonOperator = data?.experienceComparisonOperator ?? 'isBetween';
    updatedQuery.totalYearsExperienceStartRange = data.totalYearsExperienceStartRange ?? 0;
    updatedQuery.totalYearsExperienceEndRange = data.totalYearsExperienceEndRange ?? 0;
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    return updatedQuery;
  };
  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setQuery({ ...query });
  };

  const onApplyFilters = (
    data: TalentPoolAdvanceFilterFormProps,
    searchData: TalentPoolSearchRequest
  ) => {
    setQuery(updateQuery(data, searchData));
    setPageNumber(1);
    setAdvancedFilterFields(data);
    setAppliedFilters(pushTalentPoolFilters(data));
    setSearchParams({ d: getEncodedQueryParams(updateQuery(data)) });
  };

  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    if (!candidate.isSelected) setNumberOfSelectedCandidates(numberOfSelectedCandidates + 1);
    else setNumberOfSelectedCandidates(numberOfSelectedCandidates - 1);
    candidate.isSelected = !candidate.isSelected;
  };

  const selectAllCandidates = () => {
    candidates.list
      .filter((candidate) => candidate.email && !candidate.isInDnc)
      .forEach((candidate) => {
        candidate.isSelected = true;
      });
    setNumberOfSelectedCandidates(
      candidates.list.filter((candidate) => candidate.email && candidate.isSelected).length
    );
  };

  const unselectAllCandidates = () => {
    candidates.list.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const handleDownload = async (type: string) => {
    const downloadStatus = await handleCandidateDownload(type, query, [], 'talentpool');
    if (downloadStatus == 'success') setNotification(success('Downloaded candidates'));
    else setNotification(warning('Something went wrong, Please try again.'));
  };
  const setDownloadSource = (downloadSource: string, index: number) =>
    (candidates.list[index].downloadSource = downloadSource);
  const onCVClick = (index: number) => {
    if (job && getCachedJob() && getCachedJob().id !== job.id) {
      cacheJob(job);
      updateSelectedJob(job);
    }
    updateFromPage('talentpool');
    setCvViewSourcePage(ITrackCvPages.TALENTPOOL);
    setCvIndex(index);
    // openModal(candidates.list[index]);
  };

  const handleOpenSingleEmail = (candidate: ICandidate, email: string) => {
    updateFromPage('talentpool');
    setSelectedEmail(email);
    openModal(candidate);
  };

  const clearFilters = () => {
    setAppliedFilters([]);
    setSearchParams({ d: getEncodedQueryParams(defaultRequest) });
    setSearchFilterDefaultValues(setDefaultCandidateSearchFilters(defaultRequest));
    setAdvancedFilterFields(setDefaultCandidateAdvanceFilters(defaultRequest));
    setQuery({ ...defaultRequest });
  };

  const handleSearchScope = (value) => {
    setSearchParams({ d: getEncodedQueryParams({ ...query, searchScope: value }) });
    setQuery({ ...query, searchScope: value });
    setSearchFilterDefaultValues(
      setDefaultCandidateSearchFilters({ ...query, searchScope: value })
    );
  };

  const openShare = (candidate: ICandidate) => {
    selectedCandidate.current = candidate;
    selectedJob ? setOpenShareCandidateEmail(true) : setOpenJob(true);
  };

  const updateToggleButton = async () => await setIsToggleButtonActive(!isToggleButtonActive);
  const selectedCandidate = useRef<ICandidate>({} as ICandidate);
  return (
    <Box>
      <LayoutTheme>
        <ShepherdTour steps={talentpoolSteps} tourOptions={tourOptions}>
          <TalentpoolHeader job={job} getJob={(e) => navigate(`/candidates/${e.id}`)} />
          <Grid container display="flex" justifyContent="space-between">
            <Grid item xs={9}>
              <Box mt={2}>
                <TalentPoolSearch
                  key={searchFilterDefaultValues.query}
                  onSort={onSort}
                  onSearch={search}
                  handleDownload={handleDownload}
                  searchFilterdefaultValues={searchFilterDefaultValues}
                  clearAdvanceFilters={clearFilters}
                  onSearchScope={handleSearchScope}
                  isToggleButtonActive={isToggleButtonActive}
                  updateToggleButton={updateToggleButton}
                />
                <ActionsFiltersWithTitle
                  recruiterActions={query.recruiterActions ?? []}
                  candidateResponses={query.candidateResponses ?? []}
                />
                <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                  <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
                </ErrorBoundary>
              </Box>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {candidates.list.length > 0 && (
                  <Box my={2}>
                    <WsPagination
                      count={Math.ceil(
                        candidates.totalCandidates /
                          Number(paginationSettings.talentBenchPagination)
                      )}
                      onChange={pageChange}
                      itemsPerPage={Number(paginationSettings.talentBenchPagination)}
                      pageNumber={query.page}
                      defaultPage={pageNumber}
                      isPageCount={true}
                      type="Candidates"
                      totalCount={candidates.totalCandidates}
                    />
                  </Box>
                )}
              </ErrorBoundary>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                {candidates.list.length > 0 && !loader && !candidates.isError ? (
                  <Box>
                    <Box>
                      {candidates.list?.map((candidate, index) => (
                        <Box my={2} key={index}>
                          <TalentPoolCandidateCard
                            job={job}
                            candidate={candidate}
                            isSelected={candidate.isSelected}
                            checkBoxOnclick={() => addSelectedCandidate(candidate)}
                            candidateIndex={index}
                            setDownloadSource={setDownloadSource}
                            onCVClick={onCVClick}
                            onActionClick={(index) => setCvIndex(index)}
                            openSingleEmail={handleOpenSingleEmail}
                            queryRefresh={queryRefresh}
                            interaction={getInteraction(candidates.stageStatus, candidate.email)}
                            onOpenShareIcon={() => openShare(candidate)}
                            array={candidates.list?.map((candidate) => candidate.id ?? '') ?? ['']}
                            backwardpathArray={generateBackwardURL('Talent Pool')}
                          />
                        </Box>
                      ))}
                    </Box>
                    <Grid item xs={12} container justifyContent="flex-end" mt={2}>
                      {candidates.totalCandidates > 0 && (
                        <WsPagination
                          count={Math.ceil(
                            candidates.totalCandidates /
                              Number(paginationSettings.talentBenchPagination)
                          )}
                          onChange={pageChange}
                          itemsPerPage={Number(paginationSettings.talentBenchPagination)}
                          pageNumber={query.page}
                          defaultPage={pageNumber}
                          isPageCount={false}
                          type="Candidates"
                          totalCount={candidates.totalCandidates}
                        />
                      )}
                      <Box mt={1} mb={8}>
                        <NoDataMsg
                          title="Results exhausted"
                          msg="You have exhausted the search results. If you want more, consider broadening your search query with different terms or less specific criteria to explore a wider range of results."
                        />
                      </Box>
                    </Grid>
                  </Box>
                ) : loader ? (
                  <WsListingLoader />
                ) : candidates.isError ? (
                  <Box>
                    <NoDataMsg
                      title="No Matches Found"
                      msg={`Your search - "${query.query}" - did not return any matching candidates from your talentpool.`}
                    />
                  </Box>
                ) : (
                  <NoDataMsg
                    title="No Matches Found"
                    msg={`Your search - "${query.query}" - did not return any matching candidates from your talentpool.`}
                  />
                )}
              </ErrorBoundary>
            </Grid>
            <Grid item xs={2.8}>
              <TalentPoolAdvancesFiltersNew
                onApplyFilters={onApplyFilters}
                defaultValues={advancedFilterDefaultValues}
              />
            </Grid>
          </Grid>
        </ShepherdTour>
      </LayoutTheme>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {numberOfSelectedCandidates > 0 && (
          <CandidateBulkSelectionBar
            selectedCandidates={numberOfSelectedCandidates}
            selectAllCandidates={selectAllCandidates}
            unselectAllCandidates={unselectAllCandidates}
            openBulkSms={() => setOpenBulkSms(true)}
            openEmail={() => {
              updateFromPage('talentpool');
              setOpenEmail(true);
            }}
            onBulkSubmitClick={() => {
              setSubmissionSettings();
              setOpenSubmitCandidate(true);
            }}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openBulkSms && (
          <BulkSmsDrawer
            open={openBulkSms}
            handleClose={() => {
              setOpenBulkSms(false);
              handleSelectedCandidatesLimit(candidates);
              setNumberOfSelectedCandidates(0);
            }}
            candidates={candidates.list.filter((candidate) => candidate.isSelected)}
          />
        )}
      </ErrorBoundary>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        {openEmail && (
          <BulkEmail
            ignoreDuplicateCheck={true}
            open={openEmail}
            handleClose={() => {
              setOpenEmail(false);
              handleSelectedCandidatesLimit(candidates);
              setNumberOfSelectedCandidates(0);
            }}
            candidates={candidates.list.filter(
              (candidate) =>
                candidate.isSelected &&
                candidate.email &&
                !candidate.isInDnc &&
                (candidate.downloadSource = 'TALENTPOOL_SEARCH')
            )}
            drawer={true}
          />
        )}
      </ErrorBoundary>
      <WsDrawer
        hideHeader={true}
        open={openJob || updateActionChangeJob}
        size="medium"
        onClose={() => {
          setOpenJob(false);
          setupdateActionChangeJob(false);
        }}
      >
        <JobSelection
          getJob={(job) => {
            updateSelectedJob(job);
            setOpenJob(false);
            setOpenShareCandidateEmail(true);
            setupdateActionChangeJob(false);
          }}
          onBackButton={() => {
            setOpenJob(false);
            setupdateActionChangeJob(false);
          }}
        />
      </WsDrawer>
      <WsDrawer
        open={openShareCandidateEmail && !openJob && !updateActionChangeJob}
        size="medium"
        onClose={() => setOpenShareCandidateEmail(false)}
      >
        <ShareCandidateOverlay
          selectedJobData={selectedJob || null}
          onClose={() => setOpenShareCandidateEmail(false)}
          candidate={selectedCandidate.current}
        />
      </WsDrawer>
      {openSubmitCandidate && submissionSetting && (
        <CandidateBulkSubmissions
          candidatesArray={candidates.list.filter(
            (candidate) => candidate.isSelected && candidate.email && !candidate.isInDnc
          )}
          job={getCachedJob() ?? selectedJob ?? job ?? null}
          close={() => {
            setOpenSubmitCandidate(false);
            unselectAllCandidates();
          }}
          openCandidateBulkSubmissions={openSubmitCandidate}
          submissionSetting={submissionSetting}
        />
      )}
    </Box>
  );
};
export default TalentPoolListing;
